import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { isSelfRegistrationEnabled } from '@/lib/registration';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

    const verifyMutation = useMutation({
        mutationFn: async (token: string) => {
            const response = await api.get(`/auth/verify-email?token=${token}`);
            return response.data;
        },
        onSuccess: () => {
            setStatus('success');
        },
        onError: () => {
            setStatus('error');
        },
    });

    useEffect(() => {
        if (token) {
            verifyMutation.mutate(token);
        } else {
            setStatus('error');
        }
    }, [token]);

    return (
        <div className="min-h-screen bg-linear-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md p-8">
                <div className="text-center">
                    {status === 'loading' && (
                        <>
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Verifying Your Email...
                            </h1>
                            <p className="text-gray-600">
                                Please wait while we verify your email address.
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Email Verified!
                            </h1>
                            <p className="text-gray-600 mb-6">
                                Your email has been successfully verified. You can now sign in to
                                your account.
                            </p>
                            <Link to="/">
                                <Button className="w-full">Go to Login</Button>
                            </Link>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                                <XCircle className="w-10 h-10 text-red-600" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Verification Failed
                            </h1>
                            <p className="text-gray-600 mb-6">
                                This verification link is invalid or has expired. Please request a
                                new verification email.
                            </p>
                            <div className="space-y-3">
                                {isSelfRegistrationEnabled() && (
                                    <Link to="/register">
                                        <Button className="w-full" variant="outline">
                                            Create New Account
                                        </Button>
                                    </Link>
                                )}
                                <Link to="/">
                                    <Button className="w-full">Back to Login</Button>
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default VerifyEmail;
